import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/auth-provider";
import { useEffect } from "react";
import { toast } from "@/hooks/use-toast";

interface Organization {
  id: string;
  subdomain: string;
  name: string;
  display_name?: string;
  logo_url?: string;
  theme_config: any;
  is_active: boolean;
}

interface OrganizationMembership {
  id: string;
  user_id: string;
  org_id: string;
  role: 'admin' | 'member' | 'guest';
}

export const useSubdomainOrganization = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const getSubdomainFromUrl = () => {
    if (typeof window === 'undefined') return 'swift';
    
    const hostname = window.location.hostname;
    console.log('Current hostname:', hostname);
    
    // Production subdomain extraction (HIGHEST PRIORITY for security)
    if (hostname !== 'localhost' && hostname !== '127.0.0.1' && 
        !hostname.includes('.lovableproject.com') &&
        !hostname.includes('.sandbox.lovable.dev') &&
        !hostname.includes('vercel.app')) {
      const parts = hostname.split('.');
      if (parts.length >= 3) {
        const subdomain = parts[0];
        console.log('Using production subdomain:', subdomain);
        return subdomain;
      }
    }
    
    // Check URL parameters for organization override (development/testing)
    const urlParams = new URLSearchParams(window.location.search);
    const orgParam = urlParams.get('org');
    if (orgParam) {
      console.log('Using org parameter:', orgParam);
      return orgParam;
    }
    
    // Check localStorage for development organization preference
    const devOrg = localStorage.getItem('dev-organization');
    if (devOrg) {
      console.log('Using localStorage org:', devOrg);
      return devOrg;
    }
    
    // Default for development
    return 'swift';
  };

  const subdomain = getSubdomainFromUrl();

  // Fetch organization by subdomain
  const { data: organization, isLoading: orgLoading, error: orgError } = useQuery({
    queryKey: ['organization', subdomain],
    queryFn: async (): Promise<Organization> => {
      console.log('Fetching organization for subdomain:', subdomain);
      
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('subdomain', subdomain)
        .eq('is_active', true)
        .single();

      if (error) {
        console.error('Organization fetch error:', error);
        
        // If organization not found, try fallback to Swift
        if (error.code === 'PGRST116') {
          console.log('Organization not found, falling back to Swift');
          
          const { data: swiftOrg, error: swiftError } = await supabase
            .from('organizations')
            .select('*')
            .eq('subdomain', 'swift')
            .eq('is_active', true)
            .single();
          
          if (swiftError) {
            console.error('Swift fallback error:', swiftError);
            throw new Error('No valid organization found');
          }
          
          return swiftOrg;
        }
        throw error;
      }

      console.log('Found organization:', data);
      return data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  // Check user's membership in the current organization
  const { data: membership, isLoading: membershipLoading, error: membershipError } = useQuery({
    queryKey: ['organization-membership', user?.id, organization?.id],
    queryFn: async (): Promise<OrganizationMembership | null> => {
      if (!user?.id || !organization?.id) return null;
      
      console.log('Checking membership for user:', user.id, 'in org:', organization.id);
      
      const { data, error } = await supabase
        .from('organization_user_roles')
        .select('*')
        .eq('user_id', user.id)
        .eq('org_id', organization.id)
        .maybeSingle();

      if (error) {
        console.error('Membership check error:', error);
        throw error;
      }

      console.log('Found membership:', data);
      return data;
    },
    enabled: !!user?.id && !!organization?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Auto-assign user to organization if they're not a member
  const assignUserToOrganization = async (orgId: string, role: 'member' | 'guest' = 'member') => {
    if (!user?.id) return;
    
    try {
      console.log('Auto-assigning user to organization:', orgId, 'with role:', role);
      
      const { error } = await supabase
        .from('organization_user_roles')
        .insert({
          user_id: user.id,
          org_id: orgId,
          role: role,
        });

      if (error) {
        console.error('Auto-assignment error:', error);
        throw error;
      }

      // Invalidate membership query to refresh data
      queryClient.invalidateQueries({
        queryKey: ['organization-membership', user.id, orgId]
      });

      toast({
        title: "Welcome!",
        description: `You've been added to ${organization?.display_name || organization?.name}`,
      });

      console.log('User successfully auto-assigned to organization');
    } catch (error) {
      console.error('Failed to auto-assign user:', error);
      toast({
        title: "Access Required",
        description: "Please contact an administrator to request access to this organization.",
        variant: "destructive",
      });
    }
  };

  // Effect to handle auto-assignment
  useEffect(() => {
    const shouldAutoAssign = () => {
      // Only auto-assign if:
      // 1. User is authenticated
      // 2. Organization is loaded
      // 3. User is not already a member
      // 4. No errors occurred
      return user && 
             organization && 
             !membershipLoading && 
             !membership && 
             !membershipError &&
             !orgError;
    };

    if (shouldAutoAssign()) {
      console.log('Conditions met for auto-assignment');
      // Auto-assign as 'guest' by default for security
      // Organizations can upgrade users to 'member' or 'admin' as needed
      assignUserToOrganization(organization!.id, 'guest');
    }
  }, [user, organization, membership, membershipLoading, membershipError, orgError]);

  // Helper to check if user has access to the organization
  const hasOrganizationAccess = () => {
    return !!membership && !membershipError; // Must have membership and no errors
  };

  // Helper to get user's role in the organization
  const getOrganizationRole = () => {
    return membership?.role || 'guest';
  };

  // Helper to check if user is admin in the organization
  const isOrganizationAdmin = () => {
    return membership?.role === 'admin';
  };

  return {
    organization,
    membership,
    subdomain,
    isLoading: orgLoading || membershipLoading,
    error: orgError || membershipError,
    hasOrganizationAccess: hasOrganizationAccess(),
    organizationRole: getOrganizationRole(),
    isOrganizationAdmin: isOrganizationAdmin(),
    assignUserToOrganization,
  };
};